/**
 * X25519 key generation and serialization.
 *
 * This module is isomorphic: it works in Node 20+ (where `globalThis.crypto`
 * exposes the WebCrypto SubtleCrypto API natively) and in modern browsers.
 * It contains exactly one runtime check at load time: the SubtleCrypto API
 * must exist. Everything else routes through the `@hpke/dhkem-x25519`
 * implementation, which itself uses the platform-native primitives and falls
 * back to a pure-JS X25519 only if SubtleCrypto's `X25519` is unavailable
 * (Safari < 17.4, Node 18 without --experimental flags).
 *
 * See: docs/crypto/CRYPTO-IMPLEMENTATION-CHECKLIST.md §1, §4
 *      docs/crypto/ENCRYPTION-ARCHITECTURE.md §2 (Level 0)
 *      docs/decisions/ADR-002-encryption-model.md commitment 1
 *
 * Wire formats this module reads/writes:
 *   - Raw 32-byte X25519 public key    : Uint8Array length 32
 *   - Raw 32-byte X25519 private key   : Uint8Array length 32
 *   - Suite-prefixed serialized pubkey : 'v1:hpke-x25519-sha256-aes256gcm:<base64url>'
 *
 * The serialized form is what gets POSTed to /users/me/public-key and stored
 * in `users.public_key`. We deliberately serialize the raw scalar (32 bytes)
 * rather than DER/SPKI so the wire format is stable across libraries and
 * across decades — RFC 7748 raw scalars are a fixed primitive.
 */
import { CryptoEnvironmentError, InvalidKeyMaterialError } from './errors.js';
import { getSuite } from './hpke.js';
import { ACTIVE_SUITE, isSupportedSuite, parseSuitePrefixed, prefixWithSuite, SuiteUnknownError, } from './suite.js';
/**
 * X25519 public key length in bytes (RFC 7748).
 */
export const X25519_PUBLIC_KEY_BYTES = 32;
/**
 * X25519 private key (scalar) length in bytes (RFC 7748).
 */
export const X25519_PRIVATE_KEY_BYTES = 32;
// ---------------------------------------------------------------------------
// Environment guard
// ---------------------------------------------------------------------------
/**
 * Returns the SubtleCrypto for the current runtime, throwing a clear error
 * if it is missing. We resolve via `globalThis.crypto.subtle` so the same
 * source file works on Node 20+ and in browsers.
 */
function getSubtle() {
    const c = globalThis.crypto;
    if (c === undefined || c.subtle === undefined) {
        throw new CryptoEnvironmentError('globalThis.crypto.subtle');
    }
    return c.subtle;
}
// ---------------------------------------------------------------------------
// Key generation
// ---------------------------------------------------------------------------
/**
 * Generate a fresh X25519 keypair. The keys are returned in raw 32-byte
 * form so the caller can decide where to persist them — typically the
 * native app moves the private key into Secure Enclave / Android Keystore
 * immediately, while the browser app re-imports as `extractable: false`
 * before any storage takes place.
 *
 * Server-side usage of this function is restricted to test fixtures and
 * the trustee/guardian onboarding helper: production user keys are
 * always generated on-device.
 */
export async function generateRecipientKeyPair() {
    // Make sure the runtime is wired up before doing real work.
    getSubtle();
    const suite = getSuite();
    const pair = await suite.kem.generateKeyPair();
    const publicKey = new Uint8Array(await suite.kem.serializePublicKey(pair.publicKey));
    const privateKey = new Uint8Array(await suite.kem.serializePrivateKey(pair.privateKey));
    if (publicKey.byteLength !== X25519_PUBLIC_KEY_BYTES) {
        throw new InvalidKeyMaterialError('X25519 public key', X25519_PUBLIC_KEY_BYTES, publicKey.byteLength);
    }
    if (privateKey.byteLength !== X25519_PRIVATE_KEY_BYTES) {
        throw new InvalidKeyMaterialError('X25519 private key', X25519_PRIVATE_KEY_BYTES, privateKey.byteLength);
    }
    return { publicKey, privateKey };
}
// ---------------------------------------------------------------------------
// Public key serialization
// ---------------------------------------------------------------------------
/**
 * Serialize a 32-byte raw X25519 public key into the canonical wire form
 *   'v1:hpke-x25519-sha256-aes256gcm:<base64url>'
 * suitable for POSTing to the server. The suite prefix lets the server
 * (and a future migration job) immediately reject a key that was generated
 * under a deprecated or unknown suite.
 */
export function exportPublicKeyForServer(publicKey) {
    if (publicKey.byteLength !== X25519_PUBLIC_KEY_BYTES) {
        throw new InvalidKeyMaterialError('X25519 public key', X25519_PUBLIC_KEY_BYTES, publicKey.byteLength);
    }
    return prefixWithSuite(ACTIVE_SUITE, publicKey);
}
/**
 * Inverse of {@link exportPublicKeyForServer}. Parses the suite prefix,
 * verifies it is supported by this build, and imports the raw bytes into
 * a SubtleCrypto CryptoKey ready for HPKE.
 *
 * Both Node 20+ and modern browsers use the same code path here because
 * `globalThis.crypto.subtle` provides the X25519 primitive in both
 * environments (Node 20.6+, Chrome 113+, Firefox 130+, Safari 17.4+).
 *
 * @throws SuiteUnknownError - if `serialized` carries a suite this build
 *   does not recognise. The caller must treat this as fatal: do not silently
 *   coerce a v2 key into a v1 operation.
 */
export async function importPublicKeyFromServer(serialized) {
    const { suiteId, payload } = parseSuitePrefixed(serialized);
    if (!isSupportedSuite(suiteId)) {
        throw new SuiteUnknownError(suiteId, 'cannot import public key under unsupported suite');
    }
    if (payload.byteLength !== X25519_PUBLIC_KEY_BYTES) {
        throw new InvalidKeyMaterialError('X25519 public key (after suite-prefix decode)', X25519_PUBLIC_KEY_BYTES, payload.byteLength);
    }
    // We only support v1 here; v2 will use a different KEM and thus a
    // different deserializer — those go in a v2 sibling module.
    if (suiteId !== ACTIVE_SUITE) {
        throw new SuiteUnknownError(suiteId, `this build only imports keys for the active suite '${ACTIVE_SUITE}'`);
    }
    // Make sure the runtime is wired up.
    getSubtle();
    const suite = getSuite();
    const cryptoKey = await suite.kem.deserializePublicKey(payload);
    return { suite: suiteId, publicKey: cryptoKey };
}
/**
 * Import a raw 32-byte X25519 private key into a SubtleCrypto CryptoKey.
 *
 * Server code must NEVER call this with a real recipient private key — the
 * threat model in §1 of ENCRYPTION-ARCHITECTURE.md depends on private keys
 * being device-bound. This helper exists for:
 *   1. Trust recovery CLI (running offline with a trustee's exported key).
 *   2. Tests.
 *
 * On the client, prefer `crypto.subtle.generateKey({ name: 'X25519' },
 * false, ['deriveBits'])` to obtain a non-extractable private key directly,
 * and never round-trip private bytes through application code.
 */
export async function importPrivateKeyForRecovery(privateKey) {
    if (privateKey.byteLength !== X25519_PRIVATE_KEY_BYTES) {
        throw new InvalidKeyMaterialError('X25519 private key', X25519_PRIVATE_KEY_BYTES, privateKey.byteLength);
    }
    getSubtle();
    const suite = getSuite();
    return suite.kem.deserializePrivateKey(privateKey);
}
